import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Everything a desk screen needs from its host, injected once: the server
 * adapter, the viewer's capability set, the program's words and rewards, where
 * the desk is mounted, and (optionally) the host's own page header. Plus the
 * data the whole desk shares: the referral queue (the nav badge counts the
 * overdue ones) and the standard fee schedule (the advance dialog previews
 * with it).
 */
import { createContext, useContext, useMemo } from 'react';
import { PageBody as DefaultPageBody, PageHeader as DefaultPageHeader } from './components/Layout';
import { useLoad, useNow } from './hooks/useLoad';
import { isFirstContactOverdue, toMillis } from './lib/time';
const DeskContext = createContext(null);
const DeskDataContext = createContext(null);
/**
 * Drop trailing '/' characters in one linear pass. Not `/\/+$/`: on a long
 * run of '/' followed by another character that regex backtracks
 * polynomially (CodeQL js/polynomial-redos).
 */
function trimTrailingSlashes(value) {
    let end = value.length;
    while (end > 0 && value.charCodeAt(end - 1) === 47 /* '/' */)
        end -= 1;
    return value.slice(0, end);
}
/** '/desk/' -> '/desk'; '' and '/' -> '' (mounted at the router root). */
export function normaliseBasePath(basePath) {
    const trimmed = trimTrailingSlashes(basePath.trim());
    if (!trimmed)
        return '';
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
export function deskPaths(basePath) {
    const base = normaliseBasePath(basePath);
    return {
        root: base || '/',
        referral: (id) => `${base}/referrals/${encodeURIComponent(id)}`,
        payouts: `${base}/payouts`,
        fees: `${base}/fees`,
        feeVariant: (key) => `${base}/fees/v/${encodeURIComponent(key)}`,
        members: `${base}/members`,
        applications: `${base}/applications`,
        settings: `${base}/settings`,
    };
}
const DeskDataProvider = ({ children }) => {
    const { api, can, program } = useDesk();
    const readsFees = can('fees.view');
    const referrals = useLoad(() => api.listReferrals());
    const feeSchedule = useLoad(() => (readsFees ? api.getFeeSchedule({}) : Promise.resolve({ feeSchedule: null })), readsFees ? 'fees' : 'no-fees');
    const now = useNow();
    const firstContactHours = program.firstContactHours;
    const overdueCount = useMemo(() => (referrals.data?.referrals ?? []).filter((r) => isFirstContactOverdue(r.stage, toMillis(r.stageChangedAt), now, firstContactHours))
        .length, [referrals.data, now, firstContactHours]);
    return (_jsx(DeskDataContext.Provider, { value: { referrals, feeSchedule, now, firstContactHours, overdueCount }, children: children }));
};
export const PartnerDeskProvider = ({ api, capabilities, program, viewer, basePath, slots, children }) => {
    const value = useMemo(() => ({
        api,
        capabilities,
        can: (c) => capabilities.has(c),
        program,
        viewer,
        paths: deskPaths(basePath),
        PageHeader: slots?.PageHeader ?? DefaultPageHeader,
        PageBody: slots?.PageBody ?? DefaultPageBody,
    }), [api, capabilities, program, viewer, basePath, slots]);
    return (_jsx(DeskContext.Provider, { value: value, children: _jsx(DeskDataProvider, { children: children }) }));
};
export function useDesk() {
    const v = useContext(DeskContext);
    if (!v)
        throw new Error('useDesk must be used inside PartnerDeskProvider');
    return v;
}
export function useDeskData() {
    const v = useContext(DeskDataContext);
    if (!v)
        throw new Error('useDeskData must be used inside PartnerDeskProvider');
    return v;
}
